/**
 * Trusted-Node filesystem ObjectStore behind `athena.storage`.
 *
 * Catalog-optional: keys resolve under a configured root. Unsupported
 * control-plane ops throw `ATHENA_STORAGE_CAPABILITY_UNSUPPORTED`.
 */

import { mkdir, readdir, readFile, rm, stat, writeFile } from "node:fs/promises";
import {
  basename,
  dirname,
  isAbsolute,
  relative,
  resolve,
  sep,
} from "node:path";

import { AthenaConfigurationError } from "../config/errors.ts";
import {
  ATHENA_STORAGE_CAPABILITY_UNSUPPORTED,
  AthenaStorageCapabilityError,
} from "./errors.ts";
import type { AthenaStorageModule } from "./module.ts";

export interface AthenaLocalStorageOptions {
  prefix?: string | null;
  root: string;
}

function capabilityError(operation: string): AthenaStorageCapabilityError {
  return new AthenaStorageCapabilityError(operation);
}

function rejectUnsupported(operation: string): Promise<never> {
  return Promise.reject(capabilityError(operation));
}

function unsupportedTree(path: string): unknown {
  const thrower = (): Promise<never> => rejectUnsupported(path);
  return new Proxy(thrower, {
    apply() {
      return rejectUnsupported(path);
    },
    get(target, property) {
      if (property === "then") {
        return;
      }
      if (typeof property === "symbol") {
        return Reflect.get(target, property);
      }
      if (typeof property === "string") {
        return unsupportedTree(`${path}.${property}`);
      }
    },
  });
}

function normalizePrefix(prefix: string | undefined | null): string {
  if (!prefix?.trim()) {
    return "";
  }
  const normalized = prefix.replace(/^\/+/, "").replace(/\/?$/, "/");
  assertSafeObjectKey(normalized.replace(/\/$/, "") || "prefix");
  return normalized;
}

export function assertSafeObjectKey(key: string): string {
  if (typeof key !== "string" || !key.trim()) {
    throw new Error("Object key is invalid: key is required");
  }
  if (key.includes("\0")) {
    throw new Error("Object key is invalid: must not contain null bytes");
  }
  if (/^[a-zA-Z]:[\\/]/.test(key)) {
    throw new Error("Object key is invalid: must not be an absolute path");
  }
  if (key.startsWith("/") || key.startsWith("\\")) {
    throw new Error("Object key is invalid: must not be an absolute path");
  }
  const cleaned = key.replace(/\\/g, "/").replace(/^\/+/, "");
  if (!cleaned) {
    throw new Error("Object key is invalid: key is required");
  }
  const segments = cleaned.split("/");
  if (segments.some((segment) => segment === "..")) {
    throw new Error('Object key must not contain ".." path segments');
  }
  return segments.filter((segment) => segment && segment !== ".").join("/");
}

function resolveUnderRoot(root: string, key: string): { abs: string; key: string } {
  const logical = assertSafeObjectKey(key);
  const resolvedRoot = resolve(root);
  const target = resolve(resolvedRoot, ...logical.split("/"));
  const rel = relative(resolvedRoot, target);
  if (rel.startsWith("..") || isAbsolute(rel)) {
    throw new Error("Object key escapes storage root");
  }
  const rootPrefix = resolvedRoot.endsWith(sep)
    ? resolvedRoot
    : `${resolvedRoot}${sep}`;
  if (target !== resolvedRoot && !target.startsWith(rootPrefix)) {
    throw new Error("Object key escapes storage root");
  }
  return { abs: target, key: logical };
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return;
}

function readString(
  record: Record<string, unknown> | undefined,
  ...keys: string[]
): string | undefined {
  if (!record) {
    return;
  }
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return;
}

async function toBytes(value: unknown): Promise<Uint8Array> {
  if (value instanceof Uint8Array) {
    return value;
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return new Uint8Array(await value.arrayBuffer());
  }
  if (typeof value === "string") {
    return new TextEncoder().encode(value);
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const candidate of [
      record.body,
      record.bytes,
      record.data,
      record.content,
      record.files,
    ]) {
      if (candidate !== undefined && candidate !== value) {
        return toBytes(candidate);
      }
    }
  }
  throw new Error("storage.file.upload requires file bytes");
}

function isBinarySource(value: unknown): boolean {
  return (
    value instanceof Uint8Array ||
    value instanceof ArrayBuffer ||
    (typeof Blob !== "undefined" && value instanceof Blob) ||
    typeof value === "string"
  );
}

function extractUploadSource(input: unknown): unknown {
  if (isBinarySource(input)) {
    return input;
  }
  const record = asRecord(input);
  if (!record) {
    return input;
  }
  const files = record.files;
  if (isBinarySource(files)) {
    return files;
  }
  if (Array.isArray(files)) {
    return files[0];
  }
  return record.body ?? files ?? input;
}

function extractKey(input: unknown, fallback?: string): string {
  if (typeof input === "string") {
    return input;
  }
  const record = asRecord(input);
  const key =
    readString(record, "storage_key", "storageKey", "key") ?? fallback;
  if (!key) {
    throw new Error("storage key is required");
  }
  return key;
}

function applyPrefix(prefix: string, key: string): string {
  if (!prefix) {
    return key;
  }
  return `${prefix}${key}`;
}

function stripPrefix(prefix: string, key: string): string {
  if (prefix && key.startsWith(prefix)) {
    return key.slice(prefix.length);
  }
  return key;
}

async function collectFiles(
  root: string,
  dir: string
): Promise<Array<{ abs: string; key: string; size: number }>> {
  const out: Array<{ abs: string; key: string; size: number }> = [];
  let entries;
  try {
    entries = await readdir(dir, { withFileTypes: true });
  } catch (error) {
    const code =
      error && typeof error === "object" && "code" in error
        ? String((error as { code: unknown }).code)
        : "";
    if (code === "ENOENT") {
      return out;
    }
    throw error;
  }
  for (const entry of entries) {
    const full = `${dir}${sep}${entry.name}`;
    if (entry.isDirectory()) {
      out.push(...(await collectFiles(root, full)));
      continue;
    }
    if (!entry.isFile()) {
      continue;
    }
    const info = await stat(full);
    const rel = relative(root, full).split(sep).join("/");
    out.push({ abs: full, key: rel, size: info.size });
  }
  return out;
}

export function createLocalStorageModule(
  options: AthenaLocalStorageOptions
): AthenaStorageModule {
  const root = options.root?.trim();
  if (!root) {
    throw new AthenaConfigurationError(
      "ATHENA_RUNTIME_CONFIG_INVALID",
      'storage.provider "local" requires a filesystem root.',
      "storage"
    );
  }
  const resolvedRoot = resolve(root);
  const prefix = normalizePrefix(options.prefix);

  const putBytes = async (logicalKey: string, body: Uint8Array) => {
    const prefixed = applyPrefix(prefix, assertSafeObjectKey(logicalKey));
    const dest = resolveUnderRoot(resolvedRoot, prefixed);
    await mkdir(dirname(dest.abs), { recursive: true });
    await writeFile(dest.abs, body);
    return dest;
  };

  const upload = async (input: unknown) => {
    const key = extractKey(input);
    const bytes = await toBytes(extractUploadSource(input));
    const dest = await putBytes(key, bytes);
    const logical = stripPrefix(prefix, dest.key);
    const now = new Date().toISOString();
    const record = asRecord(input);
    const name =
      readString(record, "name", "fileName", "original_name") ??
      basename(logical);
    const contentType = readString(record, "content_type", "mime_type");
    return {
      count: 1,
      files: [
        {
          file: {
            bucket: "local",
            content_type: contentType ?? null,
            created_at: now,
            id: logical,
            is_public: false,
            metadata: {},
            name,
            size_bytes: bytes.byteLength,
            status: "ready",
            storage_key: logical,
            updated_at: now,
          },
          fileName: name,
          storage_key: logical,
        },
      ],
      storage_key: logical,
    };
  };

  const head = async (input: unknown) => {
    const logical = assertSafeObjectKey(extractKey(input));
    const dest = resolveUnderRoot(
      resolvedRoot,
      applyPrefix(prefix, logical)
    );
    try {
      const info = await stat(dest.abs);
      if (!info.isFile()) {
        throw new Error(`Object not found: ${logical}`);
      }
      return {
        key: logical,
        size: info.size,
        storage_key: logical,
      };
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        (error as { code: unknown }).code === "ENOENT"
      ) {
        throw new Error(`Object not found: ${logical}`);
      }
      throw error;
    }
  };

  const get = async (input: unknown) => {
    const logical = assertSafeObjectKey(extractKey(input));
    const dest = resolveUnderRoot(
      resolvedRoot,
      applyPrefix(prefix, logical)
    );
    try {
      return new Uint8Array(await readFile(dest.abs));
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        (error as { code: unknown }).code === "ENOENT"
      ) {
        throw new Error(`Object not found: ${logical}`);
      }
      throw error;
    }
  };

  const remove = async (input: unknown) => {
    const logical = assertSafeObjectKey(extractKey(input));
    const dest = resolveUnderRoot(
      resolvedRoot,
      applyPrefix(prefix, logical)
    );
    await rm(dest.abs, { force: true });
    return { deleted: true, storage_key: logical };
  };

  const list = async (input?: unknown) => {
    const record = asRecord(input);
    const rawPrefix = readString(record, "prefix") ?? "";
    const filter = rawPrefix
      ? assertSafeObjectKey(rawPrefix)
      : "";
    const files = await collectFiles(resolvedRoot, resolvedRoot);
    const listed = files
      .map((file) => ({
        ...file,
        key: stripPrefix(prefix, file.key),
      }))
      .filter((file) => {
        if (prefix && !file.key && file.key !== "") {
          return false;
        }
        if (!filter) {
          return true;
        }
        return (
          file.key === filter ||
          file.key.startsWith(`${filter}/`) ||
          file.key.startsWith(filter)
        );
      })
      .map((file) => ({
        id: file.key,
        name: basename(file.key),
        size_bytes: file.size,
        storage_key: file.key,
      }));
    return {
      count: listed.length,
      files: listed,
    };
  };

  const file = {
    delete: remove,
    get,
    head,
    list,
    retention: unsupportedTree("file.retention"),
    upload,
    uploadMultipart: () => rejectUnsupported("file.uploadMultipart"),
  };

  const object = {
    delete: remove,
    head,
    list,
    uploadUrl: () => rejectUnsupported("object.uploadUrl"),
    url: () => rejectUnsupported("object.url"),
  };

  const permissions = {
    grant: () => rejectUnsupported("permissions.grant"),
    list: () => rejectUnsupported("permissions.list"),
    revoke: () => rejectUnsupported("permissions.revoke"),
  };

  const base = {
    file,
    files: {
      upload: () => rejectUnsupported("files.upload"),
    },
    object,
    permission: unsupportedTree("permission"),
    permissions,
    catalog: unsupportedTree("catalog"),
    connections: unsupportedTree("connections"),
  };

  return new Proxy(base as unknown as AthenaStorageModule, {
    get(target, property, receiver) {
      if (property === ATHENA_STORAGE_CAPABILITY_UNSUPPORTED) {
        return ATHENA_STORAGE_CAPABILITY_UNSUPPORTED;
      }
      if (property in target) {
        return Reflect.get(target, property, receiver);
      }
      if (typeof property === "string") {
        return unsupportedTree(property);
      }
    },
  });
}
